"use client"

import * as React from "react"
import { createPortal } from "react-dom"
import { Bell } from "lucide-react"

import { CheckIcon, ShareIcon } from "@/components/ui/icons"
import { selectShowTakeover, useWheelTakeover } from "@/components/wheel/takeover"
import { selectShowCardsTakeover, useCardsTakeover } from "@/components/cards/takeover"
import { selectShowSlotTakeover, useSlotTakeover } from "@/components/slot/takeover"
import { selectAddSpin, useSpinsStore } from "@/lib/wheel/spins-store"
import { selectActiveDays, selectJustTaken, selectMenuRequest, selectSetActiveDays, selectSetShareOpen, selectShareOpen, selectTake, selectTaken, useRunStore } from "@/lib/creator-copy/run-store"
import { MiniWheel } from "@/components/wheel/reveal"
import { PICKS_MARK_PATH } from "@/components/cards/card-back"
import { CreatorShareModal } from "./share-modal"
import { PROMO_EXPIRY_DAYS, usePromotionsStore } from "@/lib/promotions/store"
import { toast } from "sonner"
import {
  MOCK_ACTIVITY,
  MOCK_MONTHLY_RUN,
  type MonthlyRunReward,
  type ActivityEvent,
} from "@/lib/creator-copy/mock"
import { cn } from "@/lib/utils"
import { QA_ENABLED } from "@/lib/creator-copy/flags"
import { CreatorAvatar } from "./avatar"

/* =============================================================================
   Creator header chips — Activity and the Monthly Run.

   Two dropdowns on the creator shell's top bar, same mechanics as the
   open-lineups chip: local open state, click-outside, absolute panel.

   ACTIVITY is what happened TO you. Every row is an event that pulls the
   reader back into an argument, so the stamp on each row names the mechanic
   (Faded you, Joined your side), not a notification category.

   MONTHLY RUN is the quest ladder. The rule it must always state: missing a
   day never resets anything, you only run out of days. A streak that resets
   punishes coming back, and coming back is the whole point of the mechanic.
   ============================================================================= */

const EYEBROW =
  "font-open-sauce text-[11px] font-semibold tracking-tight"

/** The dropdown mechanics of the open-lineups chip, shared by both menus. */
function useMenu() {
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    function onPointerDown(event: MouseEvent) {
      if (ref.current?.contains(event.target as Element)) return
      setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  return { open, setOpen, ref }
}

/* --- Activity -------------------------------------------------------------- */

const ACTIVITY_TONE: Record<ActivityEvent["tone"], string> = {
  copy: "text-primary",
  creator: "text-creator",
  gold: "text-gold",
  neutral: "text-grey-400",
}

export function ActivityRow({
  event,
  onRead,
}: {
  event: ActivityEvent
  onRead: () => void
}) {
  return (
    <button
      type="button"
      onClick={onRead}
      className={cn(
        "flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-white-50"
      )}
    >
      <CreatorAvatar name={event.actor} handle={event.handle} size="sm" />
      <span className="min-w-0 flex-1">
        {/* Read rows step down to grey-300, never below the grey-400 floor. */}
        <span className={cn("block text-[12px] leading-snug", event.unread ? "text-grey-100" : "text-grey-300")}>
          {event.text}
        </span>
        <span className="mt-1 flex items-baseline gap-1.5">
          <span className={cn(EYEBROW, ACTIVITY_TONE[event.tone])}>
            {event.kindLabel}
          </span>
          <span className="text-[11px] tabular-nums text-grey-400">
            {event.whenLabel}
          </span>
        </span>
      </span>
      {/* Unread is a still dot: the count on the chip already shouts. */}
      {event.unread && (
        <span
          aria-label="Unread"
          className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary"
        />
      )}
    </button>
  )
}

export function CreatorActivityMenu({ className }: { className?: string }) {
  const { open, setOpen, ref } = useMenu()
  // Read state lives here so the badge, the rows and Mark all read agree.
  const [readIds, setReadIds] = React.useState<string[]>([])
  const events = MOCK_ACTIVITY.map((event) =>
    readIds.includes(event.id) ? { ...event, unread: false } : event
  )
  const unread = events.filter((event) => event.unread).length

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={`Activity, ${unread} unread`}
        className={cn(
          "relative flex size-9 items-center justify-center rounded-full border transition-colors",
          open
            ? "border-white-300 bg-white-50 text-white"
            : "border-grey-700 text-grey-300 hover:border-grey-600 hover:text-white"
        )}
      >
        <Bell className="size-4" />
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 font-open-sauce text-[11px] font-semibold tabular-nums text-white">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="animate-slip-card-enter absolute right-0 top-full z-[60] mt-2 w-[340px] overflow-hidden rounded-2xl border border-grey-700 bg-grey-900 shadow-xl">
          <div className="flex items-baseline justify-between border-b border-grey-800 px-3 py-2.5">
            <span className={cn(EYEBROW, "text-primary")}>Activity</span>
            <button
              type="button"
              onClick={() => setReadIds(MOCK_ACTIVITY.map((event) => event.id))}
              className="text-[11px] text-grey-400 transition-colors hover:text-white"
            >
              Mark all read
            </button>
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {events.length === 0 && (
              <p className="px-3 py-6 text-center text-[13px] text-grey-400">No activity yet</p>
            )}
            {events.map((event) => (
              <ActivityRow
                key={event.id}
                event={event}
                onRead={() =>
                  setReadIds((current) =>
                    current.includes(event.id) ? current : [...current, event.id]
                  )
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/* --- Monthly Run ----------------------------------------------------------- */

export function MonthlyRunMenu({ className }: { className?: string }) {
  const { open, setOpen, ref } = useMenu()
  const run = MOCK_MONTHLY_RUN
  const activeDays = useRunStore(selectActiveDays)
  const taken = useRunStore(selectTaken)
  // Rewards earned and not yet taken: the bell's badge, on the chip, while
  // the menu is closed.
  const due = run.rewards.filter((reward) => !taken.includes(reward.days) && activeDays >= reward.days).length
  const shareOpen = useRunStore(selectShareOpen)
  const setShareOpen = useRunStore(selectSetShareOpen)
  // A page's "Check progress" opens this menu from outside the header.
  const menuRequest = useRunStore(selectMenuRequest)
  React.useEffect(() => {
    if (menuRequest > 0) setOpen(true)
  }, [menuRequest, setOpen])
  // Portals only after mount, so the server and the first client paint agree.
  const [onBody, setOnBody] = React.useState(false)
  React.useEffect(() => setOnBody(true), [])

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={cn(
          "relative flex items-center gap-1.5 rounded-full border px-3 py-1.5 transition-colors",
          open
            ? "border-primary bg-primary/10"
            : "border-primary/50 hover:border-primary hover:bg-primary/10"
        )}
      >
        <span className={cn(EYEBROW, "text-primary")}>Monthly Run</span>
        <span className="font-open-sauce text-[12px] font-black tabular-nums tracking-tight text-white">
          {activeDays}/{run.targetDays}
        </span>
        {!open && due > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 font-open-sauce text-[11px] font-semibold tabular-nums text-white">
            {due}
          </span>
        )}
      </button>

      {open && (
        <div className="animate-slip-card-enter absolute right-0 top-full z-[60] mt-2 w-[440px] overflow-hidden rounded-[20px] bg-grey-900 shadow-xl ring-1 ring-white/15">
          <MonthlyRunPanel onGoToBoard={() => setOpen(false)} />
        </div>
      )}
      {/* The share card lives on the body: the menu has a transform, and a
          fixed card inside it would be boxed by the menu. */}
      {onBody &&
        createPortal(
          <CreatorShareModal open={shareOpen} onClose={() => setShareOpen(false)} poster="run" />,
          document.body
        )}
    </div>
  )
}

/**
 * A tear-off calendar leaf: two binding rings over a band, the day large
 * under it. A date, not a badge. Gold once the run has reached it.
 */
function DayLeaf({ day, reached }: { day: number; reached: boolean }) {
  const body = reached ? "hsl(var(--primary))" : "hsl(var(--white-100))"
  const band = reached ? "hsl(var(--black-0) / 0.22)" : "hsl(var(--white-100))"
  const ring = reached ? "hsl(var(--white-0) / 0.6)" : "hsl(var(--grey-500))"
  const ink = reached ? "hsl(var(--white-0))" : "hsl(var(--grey-300))"
  return (
    <svg viewBox="0 0 40 44" aria-hidden className="h-11 w-10 shrink-0">
      <rect x="0" y="5" width="40" height="39" rx="8" fill={body} />
      <path d="M 8 5 H 32 A 8 8 0 0 1 40 13 V 16 H 0 V 13 A 8 8 0 0 1 8 5 Z" fill={band} />
      <rect x="10" y="0" width="4" height="10" rx="2" fill={ring} />
      <rect x="26" y="0" width="4" height="10" rx="2" fill={ring} />
      <text
        x="20"
        y="35"
        textAnchor="middle"
        fill={ink}
        fontSize="17"
        fontWeight="900"
        fontFamily="var(--font-open-sauce), sans-serif"
        letterSpacing="-0.5"
      >
        {day}
      </text>
    </svg>
  )
}

/**
 * A ticket with money on it: the stub notched at both ends, a perforation
 * before the tear, the dollar on the face. Cashback as an object.
 */
function CashTicket() {
  const body = "hsl(var(--primary))"
  const ink = "hsl(var(--white-0))"
  const perf = "hsl(var(--white-0) / 0.55)"
  return (
    <svg viewBox="0 0 44 44" aria-hidden className="size-11 shrink-0">
      <path
        d="M 6 10 H 38 A 3 3 0 0 1 41 13 V 18.5 A 3.5 3.5 0 0 0 41 25.5 V 31 A 3 3 0 0 1 38 34 H 6 A 3 3 0 0 1 3 31 V 25.5 A 3.5 3.5 0 0 0 3 18.5 V 13 A 3 3 0 0 1 6 10 Z"
        fill={body}
      />
      <line x1="31" y1="13" x2="31" y2="31" stroke={perf} strokeWidth="1" strokeDasharray="2 2" />
      <text
        x="17"
        y="27.5"
        textAnchor="middle"
        fill={ink}
        fontSize="15"
        fontWeight="900"
        fontFamily="var(--font-open-sauce), sans-serif"
      >
        $
      </text>
    </svg>
  )
}

/**
 * What the rung hands over, drawn small: the wheel at rest for a spin, a
 * drums for the three-pick pack, the six-card pack as a stack, a ticket for cash.
 * Gold once the run has reached it.
 */
function CardPack({ count }: { count: number }) {
  const reached = true
  const id = React.useId()
  const lampId = `${id}-lamp`
  // The stack behind the front card: one card per pick, each a step up and
  // to the right, so a six-pack is visibly thicker than a three-pack.
  const behind = count - 1
  const step = count === 6 ? 1.4 : 2
  const w = 22
  const h = 30
  const x0 = 11 - (behind * step) / 2
  const y0 = 7 + (behind * step) / 2
  return (
    <svg viewBox="0 0 44 44" aria-hidden className="size-11 shrink-0">
      <defs>
        <linearGradient id={lampId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={reached ? "hsl(var(--primary) / 0.55)" : "hsl(var(--white-200))"} />
          <stop offset="0.6" stopColor={reached ? "hsl(var(--grey-950))" : "hsl(var(--grey-900))"} />
          <stop offset="1" stopColor="hsl(var(--grey-950))" />
        </linearGradient>
      </defs>
      {Array.from({ length: behind }, (_, i) => (
        <rect
          key={i}
          x={x0 + (behind - i) * step}
          y={y0 - (behind - i) * step}
          width={w}
          height={h}
          rx="3"
          fill="hsl(var(--grey-900))"
          stroke="hsl(var(--white-200))"
          strokeWidth="0.8"
        />
      ))}
      <rect x={x0} y={y0} width={w} height={h} rx="3" fill={`url(#${lampId})`} stroke={reached ? "hsl(var(--primary) / 0.6)" : "hsl(var(--white-300))"} strokeWidth="0.8" />
      <g transform={`translate(${x0 + w / 2} ${y0 + h / 2}) scale(0.27) translate(-20.35 -15.5)`}>
        <path d={PICKS_MARK_PATH} fill={reached ? "hsl(var(--white-0))" : "hsl(var(--grey-300))"} />
      </g>
    </svg>
  )
}

/**
 * The drums: three windows with a card in each and the lever on the right.
 * The three-pick pack is the slot, so its thumb is the slot.
 */
function SlotThumb() {
  const reached = true
  const body = reached ? "hsl(var(--grey-950))" : "hsl(var(--grey-900))"
  const frame = reached ? "hsl(var(--primary) / 0.6)" : "hsl(var(--white-300))"
  const card = reached ? "hsl(var(--primary) / 0.35)" : "hsl(var(--white-200))"
  const ink = reached ? "hsl(var(--white-0))" : "hsl(var(--grey-300))"
  return (
    <svg viewBox="0 0 44 44" aria-hidden className="size-11 shrink-0">
      <rect x="4" y="9" width="30" height="26" rx="4" fill={body} stroke={frame} strokeWidth="0.8" />
      {[8, 16.5, 25].map((x) => (
        <g key={x}>
          <rect x={x} y="13" width="7" height="18" rx="1.5" fill="hsl(var(--grey-900))" stroke="hsl(var(--white-200))" strokeWidth="0.6" />
          <rect x={x + 1.2} y="16" width="4.6" height="12" rx="1" fill={card} />
          <g transform={`translate(${x + 3.5} 22) scale(0.09) translate(-20.35 -15.5)`}>
            <path d={PICKS_MARK_PATH} fill={ink} />
          </g>
        </g>
      ))}
      <rect x="36.5" y="15" width="2" height="12" rx="1" fill={frame} />
      <circle cx="37.5" cy="13.5" r="3" fill={reached ? "hsl(var(--primary))" : "hsl(var(--grey-500))"} />
    </svg>
  )
}

/** The word on the button that takes each kind of reward. */
const ACTION: Record<MonthlyRunReward["kind"], string> = {
  spin: "Spin",
  cards3: "Pull",
  cards6: "Open",
  cashback: "Claim",
}

function RewardThumb({ reward }: { reward: MonthlyRunReward }) {
  if (reward.kind === "spin")
    return (
      <span className="flex size-11 shrink-0 items-center justify-center rounded-[10px] bg-grey-950 ring-1 ring-inset ring-white-100">
        <MiniWheel className="size-9" />
      </span>
    )
  if (reward.kind === "cards3") return <SlotThumb />
  if (reward.kind === "cards6") return <CardPack count={6} />
  return <CashTicket />
}

/**
 * The run itself: figure, bar with the rungs on it, the month, today's
 * objective, the ladder, the share. One drawing, used by the header chip's
 * menu and by the creator console, so the two never disagree about the run.
 */
export function MonthlyRunPanel({
  onGoToBoard,
  flat = false,
  className,
}: {
  /** Pressed on the objective's button, before the page scrolls to the board. */
  onGoToBoard?: () => void
  /** On a page, not in a menu: no hero ground, no padding, no share prose. */
  flat?: boolean
  className?: string
}) {
  const run = MOCK_MONTHLY_RUN
  // The run.s live state: the day, and which rewards are already taken.
  // QA (QA_ENABLED only): a press on any track cell sets the day, so the
  // rest can be seen.
  const activeDays = useRunStore(selectActiveDays)
  const taken = useRunStore(selectTaken)
  const setActiveDays = useRunStore(selectSetActiveDays)
  const take = useRunStore(selectTake)
  const addSpin = useSpinsStore(selectAddSpin)
  const showWheel = useWheelTakeover(selectShowTakeover)
  // Test door, not a control: three quick presses on an unreached rung's
  // distance open the pack over the board. Nothing on the surface changes.
  const showCards = useCardsTakeover(selectShowCardsTakeover)
  const showSlot = useSlotTakeover(selectShowSlotTakeover)
  const taps = React.useRef<{ n: number; at: number; what: string }>({ n: 0, at: 0, what: "" })
  const tripleTap = (what: string, open: () => void) => () => {
    if (!QA_ENABLED) return
    const now = Date.now()
    const same = taps.current.what === what && now - taps.current.at < 900
    taps.current = { n: same ? taps.current.n + 1 : 1, at: now, what }
    if (taps.current.n >= 3) {
      taps.current = { n: 0, at: 0, what: "" }
      open()
    }
  }
  // Three quick presses on the figure opens the pack; on a Claimed, the drums;
  // on an unreached reward row, that row's own game.
  const tapToGo = tripleTap("cards", showCards)
  const tapClaimed = tripleTap("slot", showSlot)
  const openGame = (reward: MonthlyRunReward) => {
    if (reward.kind === "spin") showWheel()
    else if (reward.kind === "cards3") showSlot()
    else if (reward.kind === "cards6") showCards()
  }
  const tapAhead = (reward: MonthlyRunReward) => tripleTap("ahead-" + reward.days, () => openGame(reward))
  // The boost cycle: lineups counted so far; Claim hands the boost to the
  // real rewards shelf and starts the next cycle.
  const [boostLineups, setBoostLineups] = React.useState(run.boost.lineups)
  const boostReady = boostLineups >= run.boost.every
  const boostLeft = Math.max(0, run.boost.every - boostLineups)
  // Claimed: the boost is a perk on the slip.s own shelf, the one To Win
  // reads, so it is picked there like any other boost. The row then says
  // where it went and the next cycle starts.
  const claimBoost = () => {
    const id = "run-boost-" + Date.now()
    usePromotionsStore.setState((s) => ({
      ownedPerks: [
        ...s.ownedPerks,
        {
          id,
          kind: "boost",
          value: run.boost.multiplier,
          sourceBagId: "monthly-run",
          sourceLabel: run.boost.multiplier + "x boost · Monthly Run",
          expiresAt: Date.now() + PROMO_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
        },
      ],
    }))
    setBoostLineups(0)
    toast.success(run.boost.multiplier + "x boost is in your slip")
  }
  // Taking a reward: a spin goes to the wheel.s wallet and the wheel opens;
  // a pack opens the cards; cashback is credited. The row leaves either way.
  // What is due (else what was just taken today), and under it the next
  // reward ahead with its distance, so the road never looks empty.
  const justTaken = useRunStore(selectJustTaken)
  // Share opens the run card in the product.s share flow; the card is
  // mounted by the chip, outside this panel, so closing the menu keeps it.
  const setShareOpen = useRunStore(selectSetShareOpen)
  const due = run.rewards.filter((reward) => !taken.includes(reward.days) && activeDays >= reward.days)
  const collected = run.rewards.find((reward) => reward.days === justTaken)
  const ahead = run.rewards.find((reward) => reward.days > activeDays)
  type RowState = "due" | "collected" | "ahead"
  const rows: { reward: MonthlyRunReward; state: RowState }[] = [
    ...(due.length > 0
      ? due.map((reward) => ({ reward, state: "due" as const }))
      : collected
        ? [{ reward: collected, state: "collected" as const }]
        : []),
    ...(ahead ? [{ reward: ahead, state: "ahead" as const }] : []),
  ]
  const takeReward = (reward: MonthlyRunReward) => {
    take(reward.days)
    if (reward.kind === "spin") {
      addSpin(1)
      showWheel()
    } else if (reward.kind === "cards3") {
      showSlot()
    } else if (reward.kind === "cards6") {
      showCards()
    } else {
      toast.success("Cashback credited to your balance")
    }
  }

  return (
    <div className={cn(!flat && "p-5", className)}>
      {/* The head: the run's name, and where the month stands. */}
      {!flat && (
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-open-sauce text-[15px] font-black tracking-tight text-white">Monthly Run</span>
          <span onClick={tapClaimed} className="select-none text-[11px] font-semibold tabular-nums tracking-tight text-grey-400">
            Day {run.calendarDay} of {run.totalDays}
          </span>
        </div>
      )}

      {/* The figure: active days, spoken as a sentence. */}
      <div className={cn("flex items-baseline gap-2.5", !flat && "mt-4")}>
        <span className="font-open-sauce text-[44px] font-black leading-none tracking-[-0.04em] tabular-nums text-white">
          {activeDays}
        </span>
        <span onClick={tapToGo} className="select-none font-open-sauce text-[15px] font-semibold tracking-tight text-grey-400">
          / {run.targetDays} Active Days
        </span>
      </div>
      <p className="mt-1.5 text-[12px] leading-snug text-grey-400">Play on more days. Unlock bigger rewards.</p>

      {/* The track: one cell per active day, the rewards as the bigger cells
          with their day under them. Reached cells are pink, the rest wait. */}
      <div className="mt-4 flex items-center justify-between pb-7">
        {Array.from({ length: run.targetDays }, (_, index) => {
          const day = index + 1
          const reward = run.rewards.find((r) => r.days === day)
          const reached = day <= activeDays
          return (
            <button
              type="button"
              key={day}
              onClick={QA_ENABLED ? () => setActiveDays(day) : undefined}
              disabled={!QA_ENABLED}
              aria-label={"Day " + day}
              className="relative flex flex-col items-center disabled:cursor-default"
            >
              <span
                className={cn(
                  "block rounded-[5px]",
                  reward ? "size-6 rounded-[8px]" : "size-3",
                  reached ? (reward ? "bg-primary" : "bg-primary/60") : reward ? "bg-white-100 ring-1 ring-inset ring-white-300" : "bg-white-100"
                )}
              />
              {reward && (
                <span
                  className={cn(
                    "absolute top-full mt-1.5 text-[11px] font-semibold tabular-nums tracking-tight",
                    reached ? "text-white" : "text-grey-400"
                  )}
                >
                  {day}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* One reward row is always on the panel: every reward that is due,
          with the button that takes it; if none is due, the one just taken
          today as Collected; otherwise the next one ahead with its distance.
          A reward appears the day it is earned and leaves once taken. */}
      <div className="flex flex-col gap-2">
        {rows.map(({ reward, state }) => (
          <div
            key={reward.days}
            onClick={state === "ahead" ? tapAhead(reward) : undefined}
            className={cn(
              "flex items-center gap-3 rounded-[14px] p-3.5 ring-1 ring-inset",
              state === "due" ? "bg-primary/[0.06] ring-primary/30" : "ring-white-200"
            )}
          >
            <RewardThumb reward={reward} />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-open-sauce text-[14px] font-semibold tracking-tight text-white">
                {reward.label}
              </span>
              <span className="mt-0.5 block text-[11px] text-grey-400">
                {state === "collected"
                  ? "Collected · in your Reward Hub"
                  : reward.caption ?? reward.days + " active days" + (state === "due" ? " · Unlocked" : "")}
              </span>
            </span>
            {state === "due" ? (
              <button
                type="button"
                onClick={() => takeReward(reward)}
                className="shrink-0 rounded-full bg-primary px-4 py-1.5 font-open-sauce text-[12px] font-semibold tracking-tight text-white transition-colors hover:bg-primary/90"
              >
                {ACTION[reward.kind]}
              </button>
            ) : (
              <span
                className="shrink-0 select-none rounded-full bg-white-100 px-3.5 py-1.5 font-open-sauce text-[12px] font-semibold tabular-nums tracking-tight text-grey-300"
              >
                {state === "collected" ? "Collected" : reward.days - activeDays + " to go"}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* The standing boost: every four lineups, a 1.5x on the next entry.
          The fact on the object: the boost, the count, the cells. Claim puts
          it on the real rewards shelf, the one the wheel fills. */}
      <div className="mt-2.5 flex items-center gap-3 rounded-[14px] p-3.5 ring-1 ring-inset ring-white-200">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-primary/15 font-open-sauce text-[13px] font-black tracking-tight text-primary">
          {run.boost.multiplier}x
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-open-sauce text-[14px] font-semibold tracking-tight text-white">
            {boostReady
              ? run.boost.multiplier + "× boost on your next entry"
              : boostLeft + " more lineup" + (boostLeft === 1 ? "" : "s") + " for a " + run.boost.multiplier + "× boost"}
          </span>
          <span className="mt-0.5 block text-[11px] text-grey-400">
            Every {run.boost.every} lineups, automatically.
          </span>
        </span>
        {boostReady ? (
          <button
            type="button"
            onClick={claimBoost}
            className="shrink-0 rounded-full bg-primary px-4 py-1.5 font-open-sauce text-[12px] font-semibold tracking-tight text-white transition-colors hover:bg-primary/90"
          >
            Claim
          </button>
        ) : (
          <span className="flex shrink-0 items-center gap-1">
            {Array.from({ length: run.boost.every }, (_, i) => (
              <span
                key={i}
                className={cn("size-2.5 rounded-[3px]", i < boostLineups ? "bg-primary" : "bg-white-100")}
              />
            ))}
          </span>
        )}
      </div>

      {/* The foot: what counts, and the share. Publishing progress is the
          run's share moment, and it is real: the X composer opens with the
          count written out. */}
      {!flat && (
        <div className="mt-5 flex items-center justify-between gap-4 border-t border-white-100 pt-4">
          <p className="max-w-[260px] text-[12px] leading-snug text-grey-400">
            An active day is one real lineup settled.
          </p>
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="flex shrink-0 items-center gap-1 font-open-sauce text-[13px] font-semibold leading-none tracking-tight text-white transition-colors hover:text-primary"
          >
            Share
            <ShareIcon className="h-[13px] w-auto -translate-y-px" />
          </button>
        </div>
      )}
    </div>
  )
}
